import { FC, FormEvent } from 'react';
import styles from './DocumentTable.module.scss';

export interface FileItemData {
  name: string;
  type: 'PDF' | 'DWG' | 'DOC' | string;
  href?: string;
}

export interface DocumentRowData {
  id: string;
  source: string;
  description: string;
  files: FileItemData[];
}

export interface DocumentTableProps {
  subheading?: string;
  showFilterBar?: boolean;
  filterPlaceholder?: string;
  resultCountText?: string;
  columnLayout?: 'separateType' | 'inlineBadge';
  columns?: string[];
  rows: DocumentRowData[];
  ariaLabel?: string;
}

export const DocumentTable: FC<DocumentTableProps> = ({
  subheading,
  showFilterBar = false,
  filterPlaceholder = 'Filter files in this section',
  resultCountText,
  columnLayout = 'separateType',
  columns,
  rows,
  ariaLabel = 'Document records',
}) => {
  const defaultColumns =
    columnLayout === 'separateType'
      ? ['SOURCE', 'DESCRIPTION', 'FILE', 'TYPE']
      : ['SOURCE', 'DESCRIPTION', 'FILE'];

  const headers = columns || defaultColumns;

  const handleFilterSubmit = (e: FormEvent) => {
    e.preventDefault();
  };

  return (
    <div className={styles.tableContainer}>
      {showFilterBar && (
        <div className={styles.filterBar}>
          <form className={styles.filterControls} role="search" onSubmit={handleFilterSubmit}>
            <input
              type="search"
              className={styles.filterInput}
              placeholder={filterPlaceholder}
              aria-label={filterPlaceholder}
            />
            <button type="button" className={styles.filterChip} aria-haspopup="listbox">
              <span>Year: All</span>
              <span className={styles.chipIndicator} aria-hidden="true">▾</span>
            </button>
            <button type="button" className={styles.filterChip} aria-haspopup="listbox">
              <span>File type: All</span>
              <span className={styles.chipIndicator} aria-hidden="true">▾</span>
            </button>
          </form>
          {resultCountText && <span className={styles.resultCount}>{resultCountText}</span>}
        </div>
      )}

      {subheading && (
        <div className={styles.tableHeaderSection}>
          <h3 className={styles.subheading}>{subheading}</h3>
        </div>
      )}

      <div className={styles.tableWrapper}>
        <table className={styles.table} aria-label={ariaLabel}>
          <thead>
            <tr>
              {headers.map((header) => (
                <th key={header} scope="col">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td className={styles.sourceCell}>{row.source}</td>
                <td className={styles.descriptionCell}>{row.description}</td>
                {columnLayout === 'separateType' ? (
                  <>
                    <td>
                      {row.files.map((file, idx) => (
                        <div key={`${file.name}-${idx}`} className={styles.fileItem}>
                          <a
                            href={file.href || "/more-info"}
                            className={styles.fileLink}
                          >
                            {file.name}
                          </a>
                        </div>
                      ))}
                    </td>
                    <td>
                      {row.files.map((file, idx) => (
                        <span
                          key={`${file.type}-${idx}`}
                          className={`${styles.typeBadge} ${
                            file.type === 'PDF' ? styles.pdf : file.type === 'DOC' ? styles.doc : styles.dwg
                          }`}
                        >
                          {file.type}
                        </span>
                      ))}
                    </td>
                  </>
                ) : (
                  <td>
                    <div className={styles.fileList}>
                      {row.files.map((file, idx) => (
                        <div key={`${file.name}-${idx}`} className={styles.fileItem}>
                          <span
                            className={`${styles.typeBadge} ${
                              file.type === 'PDF' ? styles.pdf : file.type === 'DOC' ? styles.doc : styles.dwg
                            }`}
                          >
                            {file.type}
                          </span>
                          <a
                            href={file.href || "/more-info"}
                            className={styles.fileLink}
                          >
                            {file.name}
                          </a>
                        </div>
                      ))}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
